// External libraries
import {createRouter, createWebHistory} from 'vue-router';
import {jwtDecode} from 'jwt-decode';
import apiService from '@/services/apiService';

// Layout components
import HomePage from '../components/layout/HomePage.vue';
import AboutPage from '../components/layout/AboutPage.vue';
import ShowLoginsPage from '../components/layout/ShowLoginsPage.vue';
import OrganizationsMgmtPage from '../components/layout/OrganizationsMgmtPage.vue';
import UserMgmtPage from '../components/layout/UserMgmtPage.vue';
import ProfilePage from '../components/layout/ProfilePage.vue';

// Login components
import LogoutConfirmationPage from '../components/login/LogoutConfirmationPage.vue';
import LoginPage from '../components/login/LoginPage.vue';
import LogoutPage from '../components/login/LogoutPage.vue';


// Plot components
import AlbumProfile from '@/components/layout/AlbumProfile.vue';
import AlbumView from '@/components/layout/AlbumView.vue';
import ImageUpload from '@/components/layout/ImageUpload.vue';
import ImageDetail from '@/components/layout/ImageDetail.vue';
import ContestsPage from '@/components/layout/ContestsPage.vue';
import CreateContestPage from '@/components/layout/CreateContestPage.vue';
import ContestDetail from '@/components/layout/ContestDetail.vue';
import ContestSubmitImage from '@/components/layout/ContestSubmitImage.vue';
import RegistrationMgmtPage from "@/components/layout/RegistrationMgmtPage.vue";
import SignupPage from "@/components/layout/SignupPage.vue";
import ModerationPage from "@/components/layout/ModerationPage.vue";
import DiscoverPage from "@/components/layout/DiscoverPage.vue";


const routes = [
  // Layout routes
  {
    path: '/',
    name: 'Home',
    component: HomePage,
    meta: {
      title: 'Home'
    }
  },
  {
    path: '/discover',
    name: 'Discover',
    component: DiscoverPage,
    meta: {
      title: 'Discover',
    }
  },
  {
    path: '/albums',
    name: 'AlbumProfile',
    component: AlbumProfile,
    meta: {
      title: 'My Albums'
    },
    children: [
      // TO DO
    ]
  },
  {
    path: '/albums/:id',
    name: 'AlbumView',
    component: AlbumView,
    props: true,
    meta: {
      title: 'Album view',
      requiresAuth: true
    }
  },
  {
    path: '/albums/:id/upload',
    name: 'ImageUpload',
    component: ImageUpload,
    props: true,
    meta: {
      title: 'Upload an image',
      requiresAuth: true
    }
  },
  {
    path: '/images/:image_id',
    name: 'ImageDetail',
    component: ImageDetail,
    props: true,
    meta: {
      title: 'Image detail',
      requiresAuth: true
    }
  },
  {
    path: '/contests',
    name: 'ContestsPage',
    component: ContestsPage,
    props: true,
    meta: {
      title: 'Contests',
      requiresAuth: true
    }
  },
  {
    path: '/contests/create',
    name: 'CreateContestPage',
    component: CreateContestPage,
    props: true,
    meta: {
      title: 'Create Contest',
      allowedRoles: ['ADMIN']
    }
  },
  {
    path: '/contests/:id',
    name: 'ContestDetail',
    component: ContestDetail,
    props: true,
    meta: {
      title: 'Contest',
      requiresAuth: true
    }
  },
  {
    path: '/contests/:id/submit',
    name: 'ContestSubmitImage',
    component: ContestSubmitImage,
    props: true,
    meta: {
      title: 'Submit',
      requiresAuth: true
    }
  },
  {
    path: '/about',
    name: 'AboutPage',
    component: AboutPage,
    meta: {
      title: 'About'
    }
  },
  {
    path: '/showloginspage',
    name: 'ShowLoginsPage',
    component: ShowLoginsPage,
    meta: {
      title: 'Logins',
      requiresAuth: true,
      allowedRoles: ["ADMIN", "ORG_ADMIN"]
    }
  },
  {
    path: '/myorganizationspage',
    name: 'MyOrganizationsPage',
    component: OrganizationsMgmtPage,
    meta: {
      title: 'Organizations',
      requiresAuth: true,
      allowedRoles: ['ADMIN', 'ORG_ADMIN']
    }
  },
  {
    path: '/user-mgmt',
    name: 'UserMgmtPage',
    component: UserMgmtPage,
    meta: {
      title: 'Users',
      requiresAuth: true,
      allowedRoles: ['ADMIN', 'ORG_ADMIN']
    }
  },
  {
    path: '/registration-mgmt',
    name: 'RegistrationMgmtPage',
    component: RegistrationMgmtPage,
    meta: {
      title: 'Registrations',
      requiresAuth: true,
      allowedRoles: ['ADMIN', 'ORG_ADMIN']
    }
  },
  {
    path: '/moderation',
    name: 'ModerationPage',
    component: ModerationPage,
    meta: {
      title: 'Moderation',
      requiresAuth: true,
      allowedRoles: ['ADMIN', 'MODERATOR']
    }
  },
  {
    path: '/profile',
    name: 'ProfilePage',
    component: ProfilePage,
    meta: {
      title: 'My Account',
      requiresAuth: true
    }
  },

  // Login routes
  {
    path: '/login',
    name: 'LoginPage',
    component: LoginPage,
    meta: {
      title: 'Login'
    }
  },
  {
    path: '/logout-confirmation',
    name: 'LogoutConfirmationPage',
    component: LogoutConfirmationPage,
    meta: {
      title: 'Logout'
    }
  },
  {
    path: '/logout',
    name: 'LogoutPage',
    component: LogoutPage,
    meta: {
      title: 'Logout'
    }
  },
  {
    path: '/signup',
    name: 'SignupPage',
    component: SignupPage,
    meta: {
      title: 'Signup'
    }
  }
];



const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

// Helper function to check if the token is expired
function isTokenExpired(token) {
  if (!token) return true;

  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return decodedToken.exp < currentTime;
  } catch (error) {
    console.error('Error decoding token:', error);
    return true;  // If there's an error decoding, treat it as expired
  }
}

// Helper function to check authentication using new system
function checkAuthentication() {
  const token = apiService.getAuthToken();
  const user = apiService.getCurrentUser();

  // Check if token exists and is not expired
  const isTokenValid = token && !isTokenExpired(token);

  return {
    token,
    user,
    isTokenValid,
    isAuthenticated: isTokenValid && user,
    userRoles: user ? user.roles : null
  };
}

router.beforeEach((to) => {
  const auth = checkAuthentication();
  const isStudentLoggedIn = sessionStorage.getItem('studentLoggedIn') === 'true';

  if (to.name === 'LoginStudentPage') {
    return true;
  }

  if (to.meta.requiresAuth) {
    if (!auth.isAuthenticated) {
      if (auth.token && isTokenExpired(auth.token)) {
        apiService.clearAuthData();
      }
      return { name: 'LoginPage' };
    }

    if (to.meta.allowedRoles?.length) {
      const hasAccess = auth.userRoles.some(role =>
          to.meta.allowedRoles.includes(role)
  )

  if (!hasAccess) {
    return { name: 'HomePage' }
  }
}
    return true;
  }

  if (isStudentLoggedIn) {
    return true;
  }

  return true;
});

router.afterEach((to) => {
  document.title = 'Apertura - ' + to.meta.title || 'Apertura';
})

export default router;